<template>
    <div class="[ flex flex-col gap-4 ]" v-bind="$attrs">
        <div class="[ flex justify-between items-center gap-4 ]">
            <div class="[ flex-1 flex ]">
                <h2 class="[ font-bold ]">Scripts</h2>
            </div>
            <ButtonComponent label="Create script" outlined @click="openBlankDrawer">
                <template #icon>
                    <JBIcon name="add" />
                </template>
            </ButtonComponent>
        </div>

        <DataTable :value="data" :loading="loading">
            <template #empty>
                <div>No data</div>
            </template>
            <Column field="id" header="Id">
                <template #body="{ data }: { data: ContactScriptManifest }">
                    <div>{{ data.id }}</div>
                </template>
            </Column>
            <Column field="name" header="Name">
                <template #body="{ data }: { data: ContactScriptManifest }">
                    <div>{{ data.name }}</div>
                </template>
            </Column>
            <Column>
                <template #body="{ data }: { data: ContactScriptManifest }">
                    <ButtonGroup>
                        <ButtonComponent outlined label="Edit" @click="selectRow(data)" />
                        <ButtonComponent outlined label="Logs" @click="onLogsClicked(data.id!)" />
                        <ButtonComponent outlined label="Delete" @click="confirmDelete(data.id!)" />
                    </ButtonGroup>
                </template>
            </Column>
        </DataTable>
    </div>
    <JBDialog v-model:visible="showConfirm" header="Delete script">
        <div>Are you sure you want to delete this script?</div>
        <template #footer>
            <div class="[ flex gap-3 justify-end ]">
                <ButtonComponent label="Delete" @click="commitDelete" :loading="isDeleting" />
            </div>
        </template>
    </JBDialog>
    <JBDrawer v-model="showDrawer" :title="selectedId === -1 ? 'Create Script' : `Edit Script ${name}`">
        <div class="[ flex flex-col h-full gap-4 p-x-3 ]">
            <GridForm size="full">
                <GridFormField id="name" label="Name">
                    <template #input="{ id }">
                        <InputText :id="id" v-model="name" placeholder="Script name" />
                    </template>
                </GridFormField>
                <GridFormField id="trigger" label="Trigger">
                    <template #input="{ id }">
                        <Select :id="id" v-model="trigger" :options="triggerOptions" option-label="label"
                            option-value="value" placeholder="Select a trigger" />
                    </template>
                </GridFormField>
            </GridForm>

            <MonacoEditor :model-value="source" @update:model-value="newSource = $event" :libs="libs"
                class="[ h-96 ]" />

            <div class="[ flex justify-end ]">
                <ButtonComponent :label="selectedId === -1 ? 'Create' : 'Update'" :loading="isUpdating || isCreating"
                    @click="commit" :disabled="!validForm" />
            </div>
        </div>
    </JBDrawer>
    <JBDialog header="Execution Logs" v-model:visible="showExecutionLogs">
        <ScriptExecutions :scriptId="selectedId" />
    </JBDialog>
</template>

<script setup lang="ts">
import { Column, DataTable, Button as ButtonComponent, InputText, Select, ButtonGroup } from "primevue";
import { useApi, useApiLoader } from '@/services/api';
import { ContactScriptManifest, TenantContactEvent } from '@/services/api/sdk';
import { computed, onMounted, ref } from 'vue';
import JBDrawer from "@/components/ui/modals/JBDrawer.vue";
import GridForm from "@/components/ui/GridForm.vue";
import GridFormField from "@/components/ui/GridFormField.vue";
import MonacoEditor from "@/components/ui/MonacoEditor.vue";
import JBDialog from "@/components/ui/modals/JBDialog.vue";
import ScriptExecutions from "@/components/scripts/ScriptExecutions.vue";

defineOptions({
    inheritAttrs: false,
});

definePage({
    meta: { title: 'Scripts', icon: 'file' },
});

const libs = {
    '@types/jb.d.ts': `
declare const _customerData: any[];
declare function _createGuidance(name: string, type: string, icon: string, content: string): void;
declare function _addMessage(message: any[]): void;
declare function end(): void;

declare function secondsBetween(a: Date, b: Date): number;
declare function secondsSince(date: Date): number;
declare function createGuidance(name: string, type: string, icon: string, content: JSX.Element): void;
declare function eventWasTriggered(name: string): boolean;
declare var customerData: Record<string, any>;
declare var console: Console;

declare const status: string;
declare const dateStarted: Date;
declare const dateConnected: Date;
declare const dateEnded: Date;
declare const dateClosed: Date;
declare const isInbound: boolean;
declare const customerPhoneNumber: string;
declare const systemPhoneNumber: string;
declare const locale: string;
declare const timezone: string;
declare const tags: string[];
declare const customerContactCount: number;
`
};

// REFS

const showDrawer = ref(false);
const showConfirm = ref(false);
const selectedId = ref(-1);
const name = ref('');
const trigger = ref('');
const source = ref('');
const newSource = ref('');
const events = ref<TenantContactEvent[]>([]);
const showExecutionLogs = ref(false);

// COMPUTED 

const triggerOptions = computed(() => {
    return [...events.value.map((e) => ({ label: `Event: ${e.name}`, value: `event:${e.id}` })), { label: 'Timer: Every 5s', value: 'timer:5s' }];
});

const validForm = computed(() => {
    return name.value && trigger.value && newSource.value;
});

// API

const api = useApi();

const { data, load, loading } = useApiLoader(async () => {
    const res = await api.getContactScripts();
    return res.data!;
}, []);

const { loading: isUpdating, load: updateScript } = useApiLoader(async () => {
    await api.updateContactScript({
        scriptId: selectedId.value,
        updateContactScriptManifestRequest: {
            name: name.value,
            trigger: trigger.value,
            source: newSource.value,
        },
    });
}, null);

const { loading: isCreating, load: createScript } = useApiLoader(async () => {
    await api.createContactScript({
        createContactScriptManifestRequest: {
            name: name.value,
            trigger: trigger.value,
            source: newSource.value,
        },
    });
}, null);

const { loading: isDeleting, load: deleteScript } = useApiLoader(async () => {
    await api.deleteContactScript({
        scriptId: selectedId.value,
    });
}, null);

// FUNCTION

function selectRow(data: ContactScriptManifest) {
    showDrawer.value = true;
    newSource.value = data.source || '';
    source.value = data.source || '';
    name.value = data.name || '';
    trigger.value = data.trigger || '';
    selectedId.value = data.id!;
}

function openBlankDrawer() {
    showDrawer.value = true;
    source.value = '';
    name.value = '';
    trigger.value = '';
    selectedId.value = -1;
}

function confirmDelete(id: number) {
    selectedId.value = id;
    showConfirm.value = true;
}

async function commit() {
    const action = selectedId.value === -1 ? createScript : updateScript;
    if (await action()) {
        showDrawer.value = false;
        await load();
    }
}

async function commitDelete() {
    await deleteScript();
    showConfirm.value = false;
    await load();
}

async function onLogsClicked(id: number) {
    selectedId.value = id;
    showExecutionLogs.value = true;
}

onMounted(async () => {
    load();
    const res = await api.getTenantContactEvents();
    events.value = res.data!;
});

</script>